import { useRouter } from 'next/router';
import { createContext, useContext, useMemo } from 'react';

import { getIsNotFound } from '~/api/utils/getIsNotFound';
import { useContest } from '~/domains/contest/hooks/useContest';

import type {
  ContestContext as TContestContext,
  ContestDispatchContext as TContestDispatchContext,
} from './types';

export const ContestContext = createContext<TContestContext>({
  contest: null,
  contestId: null,
  isContestLoading: false,
  isContestError: false,
  isNotFound: false,
  isNotSupported: true,
  contestError: null,
});

export const ContestDispatchContext = createContext<TContestDispatchContext>({
  contestRefetch: () => null,
});

export function ContestContextProvider({ children }: React.PropsWithChildren) {
  const router = useRouter();
  const currentQuery = router.query;

  const { contestUuid: contestId } = currentQuery;

  const fullContest = useContest();
  const { data, refetch, isLoading, isError } = fullContest;

  const stateValue: TContestContext = useMemo(
    () => ({
      contest: data,
      contestId: contestId as string,
      isContestLoading: isLoading,
      isContestError: isError,
      isNotFound: getIsNotFound(fullContest),
    }),
    [data, contestId, isLoading, isError, fullContest]
  );

  const dispatchValue: TContestDispatchContext = useMemo(
    () => ({
      contestRefetch: refetch,
    }),
    [refetch]
  );

  return (
    <ContestContext.Provider value={stateValue}>
      <ContestDispatchContext.Provider value={dispatchValue}>
        {children}
      </ContestDispatchContext.Provider>
    </ContestContext.Provider>
  );
}

export const useContestContext = () => {
  const context = useContext(ContestContext);
  if (context === undefined) {
    console.error('useContestContext must be used within a ContestContextProvider');
  }

  return context;
};

// eslint-disable-next-line import/no-unused-modules
export const useContestDispatchContext = () => {
  const context = useContext(ContestDispatchContext);
  if (context === undefined) {
    console.error('useContestDispatchContext must be used within a ContestDispatchContext');
  }

  return context;
};
